import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { loadHelp } from './helpData';

const HelpContext = createContext(null);

const isTyping = () => {
  const el = document.activeElement;
  if (!el) return false;
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
};

/** Help panel state: open/closed, the article shown in the panel, and the loaded help content. */
export function HelpProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [articleId, setArticleId] = useState(null);
  const [help, setHelp] = useState(null);
  const [error, setError] = useState(null);

  const ensureLoaded = useCallback(() => {
    loadHelp().then(
      (loaded) => {
        setHelp(loaded);
        setError(null);
      },
      (e) => setError(e)
    );
  }, []);

  const openHelp = useCallback(
    (id = null) => {
      ensureLoaded();
      setArticleId(id);
      setOpen(true);
    },
    [ensureLoaded]
  );
  const closeHelp = useCallback(() => setOpen(false), []);
  const toggleHelp = useCallback(() => (open ? setOpen(false) : openHelp(null)), [open, openHelp]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '?' && !e.repeat && !e.metaKey && !e.ctrlKey && !e.altKey && !isTyping() && !document.querySelector('[aria-modal="true"]')) {
        e.preventDefault();
        toggleHelp();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [toggleHelp]);

  const value = useMemo(
    () => ({ open, articleId, setArticleId, help, error, openHelp, closeHelp, toggleHelp, ensureLoaded }),
    [open, articleId, help, error, openHelp, closeHelp, toggleHelp, ensureLoaded]
  );
  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

export function useHelp() {
  const context = useContext(HelpContext);
  if (!context) throw new Error('useHelp must be used inside HelpProvider');
  return context;
}

/** Loads help content for components outside the panel (the Help Center page). */
export function useHelpContent() {
  const { help, error, ensureLoaded } = useHelp();
  useEffect(() => {
    if (!help) ensureLoaded();
  }, [help, ensureLoaded]);
  return { help, error, retry: ensureLoaded };
}
